// Offline entry queue — stores new performance entries locally when the device
// is offline (or the request fails) and syncs them to the server when back online.
import { api } from "@/lib/api";

const QUEUE_KEY = "ct_offline_queue";
const CHANGED_EVENT = "ct-queue-changed";

function read() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function write(list) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { count: list.length } }));
}

export function queueCount() {
  return read().length;
}

export function enqueueEntry(payload) {
  const list = read();
  const item = {
    client_id: `off_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    payload,
    created_at: new Date().toISOString(),
  };
  list.push(item);
  write(list);
  return item.client_id;
}

let syncing = false;

// Attempts to POST every queued entry. Stops on the first network failure so
// items stay in order and are retried later. Returns number synced.
export async function flushQueue() {
  if (syncing) return 0;
  if (!localStorage.getItem("ct_token")) return 0; // not logged in
  if (typeof navigator !== "undefined" && navigator.onLine === false) return 0;

  syncing = true;
  let synced = 0;
  try {
    let list = read();
    while (list.length) {
      const item = list[0];
      try {
        await api.post("/entries", item.payload);
        list = read();
        list.shift();
        write(list);
        synced += 1;
      } catch (e) {
        // Network error → keep item, retry later. Server (4xx/5xx) error →
        // drop the bad item so it doesn't block the whole queue forever.
        if (!e.response) break;
        list = read();
        list.shift();
        write(list);
      }
      list = read();
    }
  } finally {
    syncing = false;
  }
  return synced;
}

export function onQueueChange(handler) {
  const wrapped = (e) => handler(e.detail?.count ?? queueCount());
  window.addEventListener(CHANGED_EVENT, wrapped);
  return () => window.removeEventListener(CHANGED_EVENT, wrapped);
}

export function getQueue() {
  return read();
}

export function removeFromQueue(client_id) {
  write(read().filter((i) => i.client_id !== client_id));
}
